import { Box, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid } from "@mui/material";
import { useState } from "react";
import { createEntityStore, UseEntityStore } from "../../common/entityStore";
import { propCustomEnumField, propNumberField } from "../../common/entityStoreForm";
import { propCurrency, propInt, propMaxValue, propMinValue, propRequired, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { InventoryArticleDto } from "./types";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { createListStore, UseListStore } from "../../common/listStore";
import { SaleArticleDetailEditDto, SaleArticleDetailPackageItemEditDto } from "./SaleArticleDetailDialog";
import { newId } from "../../common/utils";
import { EnumItemDto } from "../../common/enum";
import { map } from "rxjs";

export const SaleArticleDetailPackageItemDialog = (props: {
    close: () => void;
    dialogProps: DialogProps;
    useStore: UseEntityStore<SaleArticleDetailEditDto>;
    packageItem: SaleArticleDetailPackageItemEditDto | null;
    useInventoryArticlesStore: UseListStore<InventoryArticleDto>;
}) => {
    const [useStore] = useState(() => createEntityStore<SaleArticleDetailPackageItemEditDto>({
        data: props.packageItem ?? { id: newId(), saleArticleId: null, quantity: null, price: null },
        onSave(itemRaw) {
            const inventoryArticleId = props.useInventoryArticlesStore.getState().items.find(i => i.saleArticles.some(s => s.id === itemRaw.saleArticleId))?.id;
            const item: SaleArticleDetailPackageItemEditDto = {
                id: itemRaw.id,
                inventoryArticleId,
                saleArticleId: itemRaw.saleArticleId!,
                quantity: itemRaw.quantity!,
                price: itemRaw.price!,
            };
            let items = props.useStore.getState().clientData.packageItems;
            if (props.packageItem != null && items.some(p => p.saleArticleId === props.packageItem!.saleArticleId)) {
                items = items.map(p => p.saleArticleId === props.packageItem!.saleArticleId ? item : p);
            } else {
                items = items.concat(item);
            }
            props.useStore.getState().setClientDataProp("packageItems", items);
            props.close();
        },
        propValidators: [
            propRequired("saleArticleId"),
            propValidValue("quantity", "quantityFormatted"),
            propRequired("quantity"),
            propMinValue("quantity", 1),
            propMaxValue("quantity", 100),
            propInt("quantity"),
            propValidValue("price", "priceFormatted"),
            propRequired("price"),
            propMinValue("price", 0),
            propMaxValue("price", 10_000),
            propCurrency("price"),
            propValueValidator("saleArticleId", p => props.useStore.getState().clientData.packageItems.some(i =>
                i.saleArticleId !== props.packageItem?.saleArticleId && i.saleArticleId === p) && "Article is already part of package."),
        ]
    }));
    const [useSaleArticlesStore] = useState(() => createListStore<EnumItemDto>({
        data: props.useInventoryArticlesStore.getState().items$.pipe(map(p => {
            var ias = props.packageItem?.inventoryArticleId != null ? p.items.filter(i => i.id === props.packageItem!.inventoryArticleId) : p.items;
            return { items: ias.flatMap(i => i.saleArticles), isFetching: p.isFetching };
        })),
    }));
    return <Dialog {...props.dialogProps} maxWidth="xs"><Box m={2}>
        <DialogTitle>Edit package item</DialogTitle>
        <DialogContent>
            <Grid container spacing={2}>
                <Grid item xs={12}>{propCustomEnumField(useStore)("saleArticleId", "Article", useSaleArticlesStore, {
                    getId: p => p.id,
                    getName: p => p.code + " " + p.name,
                })}</Grid>
                <Grid item xs={12}>{propNumberField(useStore)("quantity", "quantityFormatted", "Quantity")}</Grid>
                <Grid item xs={12}>{propNumberField(useStore)("price", "priceFormatted", "Price", { isCurrency: true })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </Box></Dialog>;
};
